import { Injectable } from '@angular/core';

import { BetSlip, PlaceBetRequest } from '@bpos/v2/bet-placement';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { zip } from 'lodash-es';

import { RewardTokenType } from '../../../../../../lib/tokens-base/token-base.models';
import { IBetslipState } from '../../../../../base/store/state';
import { BetslipType } from '../../../../../core/betslip-type';
import { ISlip } from '../../../../bet-generation/models';
import { BetAndGetToken, IRewardToken } from '../../../../reward-tokens/reward-tokens.model';
import { RewardTokensMapper } from '../../../../reward-tokens/services/reward-tokens-mapper.service';
import { calculateWinningsBoost } from '../../../../reward-tokens/services/reward-tokens.utils';
import BetslipSummaryBuilder from '../../../../summary/services/betslip-summary-builder.service';
import { RequestStrategy } from './request-strategies';

@Injectable()
export default class RewardTokensRequestStrategy implements RequestStrategy {
    private logger: SportsRemoteLogger;

    constructor(
        private rewardTokensMapper: RewardTokensMapper,
        private summaryBuilder: BetslipSummaryBuilder,
        loggerFactory: LoggerFactory,
    ) {
        this.logger = loggerFactory.getLogger('RewardTokensRequestStrategy');
    }

    isActive(state: IBetslipState): boolean {
        //we don't apply reward token on System
        return state.types.base.currentSelectedType !== BetslipType.System;
    }

    updateRequest(request: PlaceBetRequest, _: IBetslipState, slips: ISlip[]): void {
        const ensureRequestSlipPairs =
            request.betSlips.length > 0 &&
            request.betSlips.length === slips.length &&
            request.betSlips.every((betslip, i) => betslip.index === slips[i]?.index);
        if (!ensureRequestSlipPairs) {
            this.logger.error(`Reward tokens couldn't be applied to the request because request slips are not matching input slips`);

            return;
        }

        const requestSlipPairs = zip(request.betSlips, slips) as [BetSlip, ISlip][];
        for (const [requestSlip, slip] of requestSlipPairs) {
            const token = slip.rewardToken;
            if (!token) {
                continue;
            }

            switch (token.rewardTokenType) {
                case RewardTokenType.FreeBet:
                    this.freeBetRequestSlipUpdate(requestSlip, token);
                    break;
                case RewardTokenType.OddsBoost:
                    this.oddsBoostRequestSlipUpdate(requestSlip, slip, token);
                    break;
                case RewardTokenType.AccaBoost:
                    this.accaBoostRequestSlipUpdate(requestSlip, slip, token);
                    break;
                case RewardTokenType.BetAndGet:
                    this.betAndGetRequestSlipUpdate(requestSlip, token as BetAndGetToken);
                    break;

                default:
                    this.genericTokenRequestSlipUpdate(requestSlip, token);
            }
        }
    }

    private freeBetRequestSlipUpdate(requestSlip: BetSlip, token: IRewardToken) {
        requestSlip.walletBucket = undefined;
        requestSlip.promoTokens = [this.rewardTokensMapper.getPlacementModel(token, null, requestSlip.bets.length)];
    }

    private betAndGetRequestSlipUpdate(requestSlip: BetSlip, token: BetAndGetToken) {
        requestSlip.edsPromoTokens = [this.rewardTokensMapper.createEDSPromoTokens(token, requestSlip.stake.amount)];
    }

    private oddsBoostRequestSlipUpdate(requestSlip: BetSlip, slip: ISlip, token: IRewardToken) {
        const summary = this.summaryBuilder.createSlipSummary(slip);
        const winningsBoost = calculateWinningsBoost(summary).toNumber();

        requestSlip.promoTokens = [
            this.rewardTokensMapper.getPlacementModel(token, winningsBoost, requestSlip.bets.length, requestSlip.stake.amount),
        ];
    }

    private accaBoostRequestSlipUpdate(requestSlip: BetSlip, slip: ISlip, token: IRewardToken) {
        const summary = this.summaryBuilder.createSlipSummary(slip);
        const winningsBoost = summary.rewardTokenModifications!.accaBoost!.currentBoost.toNumber();

        requestSlip.promoTokens = [this.rewardTokensMapper.getPlacementModel(token, winningsBoost, requestSlip.bets.length)];
    }

    private genericTokenRequestSlipUpdate(requestSlip: BetSlip, token: IRewardToken) {
        requestSlip.promoTokens = [this.rewardTokensMapper.getPlacementModel(token, null, requestSlip.bets.length, requestSlip.stake.amount)];
    }
}
